import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Play, Download } from "lucide-react";
import { api } from "../lib/api";
import { useVendors, usePlants } from "../lib/useReference";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { FieldInput, FieldSelect } from "../components/ui/Field";

interface DpoRow {
  mrpController: string;
  materialNumber: string;
  materialDescription: string;
  poNumber?: string;
  poItem?: number;
  prNumber?: string;
  prItem?: number;
  deliveryDate: string;
  scheduledQty: number;
  deliveryQty: number;
  balanceQty: number;
  plantId: number;
  firmOrPlanned: "Firm" | "Planned";
}

/** DPO Forecast — read-only, 18-month rolling, PO/PR toggle. No write
 * endpoints exist by design (matches the source's pure pass-through). */
export function DpoForecast() {
  const [vendorId, setVendorId] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [plantId, setPlantId] = useState("");
  const [poIndicator, setPoIndicator] = useState<"PO" | "PR">("PO");
  const { data: vendors } = useVendors();
  const { data: plants } = usePlants();

  const query = useQuery({
    queryKey: ["dpo", vendorId, materialNumber, plantId, poIndicator],
    queryFn: () =>
      api.get<{ rows: DpoRow[] }>(
        `/dpo/lines?${new URLSearchParams({ poIndicator, ...(vendorId && { vendorId }), ...(materialNumber && { materialNumber }), ...(plantId && { plantId }) })}`
      ),
  });
  const rows = query.data?.rows ?? [];

  return (
    <div>
      <Card className="mb-5">
        <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          <FieldSelect label="Vendor Number" value={vendorId} onChange={(e) => setVendorId(e.target.value)} options={[{ value: "", label: "All vendors" }, ...(vendors ?? []).map((v) => ({ value: v.id, label: `${v.vendorNumber} - ${v.name}` }))]} />
          <FieldInput label="Material Number" value={materialNumber} onChange={(e) => setMaterialNumber(e.target.value)} />
          <FieldSelect label="Plant Number" value={plantId} onChange={(e) => setPlantId(e.target.value)} options={[{ value: "", label: "All plants" }, ...(plants ?? []).map((p) => ({ value: p.id, label: `${p.plantNumber} - ${p.name}` }))]} />
          <div>
            <label className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">View</label>
            <div className="mt-1 flex gap-4 text-sm">
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={poIndicator === "PO"} onChange={() => setPoIndicator("PO")} /> Purchase Orders
              </label>
              <label className="flex items-center gap-1.5">
                <input type="radio" checked={poIndicator === "PR"} onChange={() => setPoIndicator("PR")} /> Purchase Requisitions
              </label>
            </div>
          </div>
        </div>
        <div className="px-5 pb-5 flex justify-end">
          <Button icon={Play} onClick={() => query.refetch()}>
            Execute
          </Button>
        </div>
      </Card>
      <Card>
        <div className="p-4 flex items-center gap-2 border-b border-slate-100 dark:border-white/10">
          <span className="font-semibold text-sm">DPO Forecast Results</span>
          <Badge tone="blue">{poIndicator === "PO" ? "Purchase Orders" : "Purchase Requisitions"}</Badge>
          <div className="ml-auto">
            <Button variant="outline" icon={Download} onClick={() => api.download("/dpo/export", "DPO_Forecast_Result.xlsx")}>
              Export to Excel
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="data w-full text-xs">
            <thead className="bg-slate-50 dark:bg-white/5 text-slate-500">
              <tr>
                <th className="p-3 text-left">MRP Controller</th>
                <th className="p-3 text-left">Material</th>
                <th className="p-3 text-left">{poIndicator === "PO" ? "PO" : "PR"}</th>
                <th className="p-3 text-left">{poIndicator === "PO" ? "PO Item" : "PR Item"}</th>
                <th className="p-3 text-left">Delivery Date</th>
                <th className="p-3 text-right">Scheduled Qty</th>
                <th className="p-3 text-right">Delivery Qty</th>
                <th className="p-3 text-right">Balance Qty</th>
                <th className="p-3 text-left">Firm / Planned</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-white/5">
                  <td className="p-3">{r.mrpController}</td>
                  <td className="p-3">
                    {r.materialNumber} — {r.materialDescription}
                  </td>
                  <td className="p-3">{poIndicator === "PO" ? r.poNumber : r.prNumber}</td>
                  <td className="p-3">{poIndicator === "PO" ? r.poItem : r.prItem}</td>
                  <td className="p-3">{r.deliveryDate}</td>
                  <td className="p-3 text-right">{r.scheduledQty}</td>
                  <td className="p-3 text-right">{r.deliveryQty}</td>
                  <td className="p-3 text-right">{r.balanceQty}</td>
                  <td className="p-3">
                    <Badge tone={r.firmOrPlanned === "Firm" ? "green" : "slate"}>{r.firmOrPlanned}</Badge>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-slate-400">
                    {query.isLoading ? "Loading…" : "No forecast lines match this filter."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
